import { GenericCSVImportService } from './genericCSVImportService';
import { ImportHistoryService } from './importHistoryService';
import { FuelTransactionCSV, ImportResult } from '../types/fuelImport';
import { CSVConfig, ValidationSchema } from '../types/csv';
import { ColumnMapping } from '../types/supplier';
import { FuelConsumption } from '../types/fuelConsumption';
import { vehicleExtendedService } from './vehicleExtendedService';
import { fuelConsumptionExtendedService } from './fuelConsumptionExtendedService';

/**
 * Service spécifique pour l'importation CSV des consommations de carburant
 * Étend le service générique d'importation CSV
 */
export class FuelCSVImportService extends GenericCSVImportService<FuelTransactionCSV> {
  private importHistoryService: ImportHistoryService;
  
  constructor() {
    super();
    this.importHistoryService = new ImportHistoryService();
  }
  
  /**
   * Traite les transactions de carburant importées
   * @param transactions Transactions de carburant mappées
   * @param organizationId ID de l'organisation
   * @param userId ID de l'utilisateur qui effectue l'importation
   * @param importId ID de l'importation (si déjà créé)
   * @returns Résultat de l'importation
   */
  async processFuelTransactions(
    transactions: FuelTransactionCSV[],
    organizationId: string,
    userId: string,
    importId?: string
  ): Promise<ImportResult> {
    // Initialiser le résultat
    const result: ImportResult = {
      totalTransactions: transactions.length,
      processedTransactions: 0,
      skippedTransactions: 0,
      fuelConsumptionsCreated: 0,
      mileageUpdates: 0,
      unknownVehicles: [],
      errors: []
    };
    
    // Traiter chaque transaction
    for (let i = 0; i < transactions.length; i++) {
      const transaction = transactions[i];
      
      try {
        // Rechercher le véhicule par sa plaque d'immatriculation
        const vehicle = await vehicleExtendedService.getVehicleByLicensePlate(transaction.matricule, organizationId);
        
        if (!vehicle) {
          // Véhicule non reconnu
          this.addUnknownVehicle(result, transaction.matricule);
          result.skippedTransactions++;
          
          // Si un ID d'importation est fourni, enregistrer la transaction non traitée
          if (importId) {
            await this.importHistoryService.saveImportedTransaction({
              importId,
              rawData: transaction as unknown as Record<string, any>,
              processedData: transaction,
              entityId: null,
              status: 'skipped',
              lineNumber: i + 2 // +2 car ligne 1 = en-têtes, et index commence à 0
            });
          }
          
          continue;
        }
        
        // Créer la consommation de carburant
        const fuelConsumptionData: Omit<FuelConsumption, 'id' | 'createdAt'> = {
          vehicleId: vehicle.id!,
          organizationId,
          date: new Date(transaction.date),
          amount: transaction.montant,
          volume: transaction.quantite,
          supplier: transaction.lieu || transaction.produit || 'Inconnu',
          createdBy: userId
        };
        
        const fuelConsumptionId = await fuelConsumptionExtendedService.createFuelConsumption(fuelConsumptionData);
        result.fuelConsumptionsCreated++;
        
        // Mettre à jour le kilométrage du véhicule si nécessaire
        if (transaction.kilometres && transaction.kilometres > 0) {
          await vehicleExtendedService.updateVehicleMileage(vehicle.id!, transaction.kilometres, new Date(transaction.date), userId);
          result.mileageUpdates++;
        }
        
        result.processedTransactions++;
        
        // Si un ID d'importation est fourni, enregistrer la transaction traitée
        if (importId) {
          await this.importHistoryService.saveImportedTransaction({
            importId,
            rawData: transaction as unknown as Record<string, any>,
            processedData: transaction,
            entityId: vehicle.id!,
            status: 'processed',
            createdEntityId: fuelConsumptionId,
            updatedField: transaction.kilometres ? 'mileage' : undefined,
            lineNumber: i + 2
          });
        }
      } catch (error) {
        // Gérer les erreurs
        console.error(`Erreur lors du traitement de la transaction ${i + 1}:`, error);
        result.errors.push({
          line: i + 2,
          message: error instanceof Error ? error.message : 'Erreur inconnue',
          data: transaction
        });
        
        // Si un ID d'importation est fourni, enregistrer la transaction en erreur
        if (importId) {
          await this.importHistoryService.saveImportedTransaction({
            importId,
            rawData: transaction as unknown as Record<string, any>,
            processedData: transaction,
            entityId: null,
            status: 'error',
            errorMessage: error instanceof Error ? error.message : 'Erreur inconnue',
            lineNumber: i + 2
          });
        }
      }
    }
    
    return result;
  }
  
  /**
   * Ajoute un véhicule non reconnu au résultat
   * @param result Résultat de l'importation
   * @param licensePlate Plaque d'immatriculation
   */
  private addUnknownVehicle(result: ImportResult, licensePlate: string): void {
    const existingVehicle = result.unknownVehicles.find(v => v.matricule === licensePlate);
    
    if (existingVehicle) {
      existingVehicle.count++;
    } else {
      result.unknownVehicles.push({
        matricule: licensePlate,
        count: 1
      });
    }
  }
  
  /**
   * Obtient le schéma de validation pour les transactions de carburant
   * @returns Schéma de validation
   */
  getValidationSchema(): ValidationSchema {
    return {
      required: ['date', 'matricule', 'typeProduit', 'quantite', 'montant'],
      types: {
        date: 'date',
        heure: 'string',
        matricule: 'string',
        kilometresAvant: 'number',
        kilometres: 'number',
        typeProduit: 'string',
        produit: 'string',
        quantite: 'number',
        montant: 'number',
        lieu: 'string',
        numFacture: 'string'
      },
      patterns: {
        matricule: /^[A-Z0-9\s-]{2,10}$/i // Format simplifié pour les plaques d'immatriculation
      },
      custom: {
        quantite: (value) => Number(value) > 0,
        montant: (value) => Number(value) > 0
      }
    };
  }
  
  /**
   * Convertit les données brutes en transactions de carburant
   * @param rawData Données brutes du CSV
   * @param mapping Mapping des colonnes
   * @returns Transactions de carburant
   */
  convertToFuelTransactions(
    rawData: Record<string, any>[],
    mapping: ColumnMapping
  ): FuelTransactionCSV[] {
    return rawData.map(row => {
      // Valeurs par défaut pour les champs optionnels
      const result: FuelTransactionCSV = {
        date: row[mapping.date],
        heure: mapping.heure ? row[mapping.heure] : '00:00:00',
        matricule: row[mapping.matricule].trim().toUpperCase(),
        kilometresAvant: mapping.kilometresAvant ? this.parseNumber(row[mapping.kilometresAvant]) : null,
        kilometres: mapping.kilometres ? this.parseNumber(row[mapping.kilometres]) : null,
        typeProduit: row[mapping.typeProduit],
        produit: mapping.produit ? row[mapping.produit] : row[mapping.typeProduit],
        quantite: this.parseNumber(row[mapping.quantite]) || 0, // Utiliser 0 comme valeur par défaut
        montant: this.parseNumber(row[mapping.montant]) || 0, // Utiliser 0 comme valeur par défaut
        lieu: mapping.lieu ? row[mapping.lieu] : '',
        numFacture: mapping.numFacture ? row[mapping.numFacture] : undefined
      };
      
      return result;
    });
  }
  
  /**
   * Parse une valeur en nombre
   * @param value Valeur à parser
   * @returns Nombre ou null si la valeur n'est pas un nombre valide
   */
  private parseNumber(value: any): number | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }
    
    // Remplacer les virgules par des points
    if (typeof value === 'string') {
      value = value.replace(',', '.');
    }
    
    const num = Number(value);
    return isNaN(num) ? null : num;
  }
  
  /**
   * Formate une date selon le format spécifié
   * @param dateStr Chaîne de date
   * @param format Format de date
   * @returns Date formatée
   */
  formatDate(dateStr: string, format: string): Date {
    // Implémentation simplifiée, à adapter selon les besoins
    let day, month, year;
    
    switch (format) {
      case 'DD/MM/YYYY':
        [day, month, year] = dateStr.split('/').map(Number);
        break;
      case 'MM/DD/YYYY':
        [month, day, year] = dateStr.split('/').map(Number);
        break;
      case 'YYYY-MM-DD':
        [year, month, day] = dateStr.split('-').map(Number);
        break;
      default:
        throw new Error(`Format de date non supporté: ${format}`);
    }
    
    return new Date(year, month - 1, day);
  }
}

// Exporter une instance du service
export const fuelCSVImportService = new FuelCSVImportService();